import { jsonInit } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import { statusCountsSchema } from '../../../lib/statusCounts';
import {
  createTargetingBuilderMultipartInit,
  serializeTargetingBuilderFilter,
  targetingBuilderDeviceTypeSchema,
  targetingBuilderSchema,
  targetingBuildersPageSchema,
  type TargetingBuilderFilter,
} from './model';

type TargetingBuilderClientOptions = {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
};

export const liverampSegmentSchema = z.looseObject({
  id: z.union([z.string(), z.number()]).transform(String),
  segmentId: z.union([z.string(), z.number()]).transform(String).optional(),
  segmentName: z.string().optional().default(''),
  providerName: z.string().optional().default(''),
  description: z.string().nullish(),
});

const liverampSegmentsPageSchema = z.looseObject({
  content: z.array(liverampSegmentSchema).optional().default([]),
  totalElements: z.number().optional().default(0),
});

const targetingBuilderMutationResponseSchema = z.preprocess(
  (value) => (value === '' ? null : value),
  targetingBuilderSchema.nullable(),
);

export type LiverampSegment = z.infer<typeof liverampSegmentSchema>;

export function createTargetingBuilderClient({
  config,
  accessToken,
  fetcher,
}: TargetingBuilderClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  return {
    getStatusCounts: (searchTerm: string) =>
      api.request({
        path: `/api/targetings/statuses?${new URLSearchParams({ searchTerm: searchTerm.trim() })}`,
        schema: statusCountsSchema,
      }),
    list: (filter: TargetingBuilderFilter) =>
      api.request({
        path: `/api/targetings?${serializeTargetingBuilderFilter(filter)}`,
        schema: targetingBuildersPageSchema,
      }),
    get: (id: string) =>
      api.request({
        path: `/api/targetings/${encodeURIComponent(id)}`,
        schema: targetingBuilderSchema,
      }),
    listDeviceTypes: () =>
      api.request({
        path: '/api/targetings/geo/device-types',
        schema: targetingBuilderDeviceTypeSchema.array(),
      }),
    listLiverampSegments: ({
      searchTerm = '',
      segmentIds = [],
    }: {
      searchTerm?: string;
      segmentIds?: string[];
    }) => {
      const params = new URLSearchParams({
        page: '0',
        size: String(segmentIds.length || 20),
        sortProperty: 'segmentName',
        sortDirection: 'ASC',
      });
      if (searchTerm.trim()) params.set('segmentName', searchTerm.trim());
      if (segmentIds.length) params.set('segmentIds', segmentIds.join(','));
      return api.request({
        path: `/api/lr/segments?${params.toString()}`,
        schema: liverampSegmentsPageSchema,
      });
    },
    create: ({
      payload,
      bundlesFile,
      ipsFile,
    }: {
      payload: unknown;
      bundlesFile?: File | null | undefined;
      ipsFile?: File | null | undefined;
    }) =>
      api.request({
        path: '/api/targetings',
        schema: targetingBuilderMutationResponseSchema,
        init: createTargetingBuilderMultipartInit(payload, {
          bundles: bundlesFile,
          ips: ipsFile,
        }),
      }),
    update: (id: string, payload: unknown) =>
      api.request({
        path: `/api/targetings/${encodeURIComponent(id)}`,
        schema: targetingBuilderMutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
  };
}

export type TargetingBuilderClient = ReturnType<typeof createTargetingBuilderClient>;
